import {
  useFirestore,
  useFirestoreCollectionData,
  useSigninCheck,
  useUser,
} from 'reactfire'
import { collection, query, where, limit } from 'firebase/firestore'
import { Tag } from 'antd'
import { RESTRICTED_COMPANY_UIDS } from './shared/restrictedCompanies'
import { useTranslation } from 'react-i18next'

export function useProfilesSelector() {
  const { t } = useTranslation()
  const user = useUser()

  const firestore = useFirestore()

  const { data: employerCompanies } = useFirestoreCollectionData(
    query(
      collection(firestore, 'companies'),
      where('deleted', '==', false),
      where('active', '==', true),
      where('employers', 'array-contains', user.data.uid)
    ),
    { idField: 'uid' }
  )
  const { data: operatorCompanies } = useFirestoreCollectionData(
    query(
      collection(firestore, 'companies'),
      where('deleted', '==', false),
      where('active', '==', true),
      where('operator.userUid', '==', user.data.uid),
      limit(1)
    ),
    { idField: 'uid' }
  )
  const isOperator = (operatorCompanies?.length ?? 0) > 0
  const { data: contracts } = useFirestoreCollectionData(
    query(
      collection(firestore, 'contracts'),
      where('employee.userUid', '==', user.data.uid),
      where('deleted', '==', false)
    ),
    {
      idField: 'uid',
    }
  )
  const { data: taxAdvisors } = useFirestoreCollectionData(
    query(
      collection(firestore, 'taxadvisors'),
      where('deleted', '==', false),
      where('active', '==', true),
      where('managers', 'array-contains', user.data.uid)
    ),
    { idField: 'uid' }
  )

  let profiles = []

  const {
    data: { hasRequiredClaims: isAdmin },
  } = useSigninCheck({ requiredClaims: { admin: true } })

  const {
    data: { hasRequiredClaims: isImpersonating },
  } = useSigninCheck({ requiredClaims: { impersonating: true } })

  if (isAdmin) {
    profiles.push({
      key: 'admin',
      link: '/admin',
      label: 'Admin',
      managedByTaxAdvisor: false,
      notificationsContext: 'admin',
    })
  }

  if (isOperator) {
    profiles.push({
      key: `operations`,
      link: `/operations`,
      label: t('frontend:common.profiles.payrollAccounting'),
      managedByTaxAdvisor: false,
      tag: null,
      tagColor: null,
      notificationsContext: `operations`,
    })
  }

  ;(taxAdvisors ?? []).forEach((taxAdvisor) => {
    profiles.push({
      key: `tax-advisor-${taxAdvisor.uid}`,
      link: `/tax-advisor/${taxAdvisor.uid}/dashboard`,
      label: (
        <span>
          {taxAdvisor.archived && (
            <Tag color="grey">{t('frontend:common.profiles.archived')}</Tag>
          )}{' '}
          {taxAdvisor.name}
        </span>
      ),
      managedByTaxAdvisor: false,
      tag: t('frontend:common.profiles.taxAdvisor'),
      tagColor: 'blue-inverse',
      notificationsContext: `tax-advisor/${taxAdvisor.uid}`,
    })
  })
  ;(employerCompanies ?? []).forEach((company) => {
    if (isImpersonating && RESTRICTED_COMPANY_UIDS.has(company.uid)) {
      return
    }
    profiles.push({
      key: `employer-${company.uid}`,
      link: `/employer/${company.uid}/dashboard`,
      label: (
        <span>
          {company.demo && <Tag color="orange-inverse">Demo</Tag>}{' '}
          {company.archived && (
            <Tag color="grey">{t('frontend:common.profiles.archived')}</Tag>
          )}{' '}
          {company.name}
        </span>
      ),
      managedByTaxAdvisor: company.managedByTaxAdvisorUid !== null,
      tag: t('frontend:common.profiles.company'),
      tagColor: null,
      notificationsContext: `employer/${company.uid}`,
    })
  })
  ;(contracts ?? []).forEach((contract) => {
    if (
      isImpersonating &&
      RESTRICTED_COMPANY_UIDS.has(contract?.employer?.uid)
    ) {
      return
    }
    profiles.push({
      key: contract.uid,
      link: `/employee/${contract.uid}/dashboard`,
      label: contract?.employer?.name ?? '—',
      inactive: !contract.active,
      managedByTaxAdvisor: false,
      tag: t('frontend:common.profiles.employed'),
      tagColor: 'cyan',
      notificationsContext: `employee/${contract.uid}`,
    })
  })

  return profiles
}
